import { useEffect, useState } from "react"
import { usePlayerStore } from "../../stores/usePlayerStore"

// Sprint 6 — Intro narrativa del Estado Cero (paginada).
//
// Antes de mostrar el selector de firma, le damos al Tripulante un
// momento para reconocer al Orbe en el centro como una proyección de
// su propia consciencia.
//
// 2026-05-05 — Reescrito a paginación manual.
// 2026-05-05 v2 — Pasamos a auto-advance: el Tripulante deja que el
// rito fluya solo, sin tocar nada. Cada párrafo aparece en pantalla
// ~5.5s y desaparece para dar paso al siguiente. Al final aparece el
// botón ENCARNAR (único elemento que sí requiere tap). El Tripulante
// puede tocar la pantalla para acelerar el avance si quiere.
//
// 2026-05-06 — Cambios:
//   · El título "ESTADO CERO · ANTES DE LA FORMA" se ancla en la
//     parte superior y queda visible TODO el rito.
//   · El primer párrafo aparece casi de inmediato (arrancamos en
//     step=1 en lugar de step=0).
//   · Los párrafos se renderean en una franja media-superior, entre
//     el título y el orbe, para que el orbe quede como protagonista
//     visual sin choque de copy encima.

const PARAGRAPHS = [
    "Antes de ser cuerpo, eres frecuencia.",
    "Esto que ves en el centro eres tú: tu consciencia proyectada en la malla digital.",
    "El cuerpo que elijas será un vehículo temporal para conversar con otros Nodos.",
    "Después de esta experiencia regresarás a tu vehículo físico actual para aplicar todo lo sintonizado en esta proyección.",
]

const FADE_MS = 700
const STEP_AUTO_MS = 5500 // duración entre saltos automáticos

export function VoidIntro() {
    const phase = usePlayerStore((s) => s.phase)
    const voidStage = usePlayerStore((s) => s.voidStage)
    const advanceVoidStage = usePlayerStore((s) => s.advanceVoidStage)

    // 2026-05-06 — Empezamos directo en step=1 (primer párrafo). El
    // título queda visible siempre por separado, así que el "step 0"
    // solo-título dejaba al Tripulante mirando texto-cabecera ~5.5s
    // antes de ver narrativa real. Esa pausa se sentía como "no pasa
    // nada" en mobile. Pasos 1..N = párrafos. Paso N+1 = botón.
    const [step, setStep] = useState(1)

    // Reset al re-entrar a void/intro (reconfiguración).
    useEffect(() => {
        if (phase === "void" && voidStage === "intro") {
            setStep(1)
        }
    }, [phase, voidStage])

    const isVisible = phase === "void" && voidStage === "intro"

    const totalParagraphSteps = PARAGRAPHS.length // 4
    const isFinalStep = step > totalParagraphSteps // step 5+ = botón
    const currentParagraph = step >= 1 && step <= totalParagraphSteps
        ? PARAGRAPHS[step - 1]
        : null

    const advance = () => {
        if (step <= totalParagraphSteps) {
            setStep((s) => s + 1)
        }
    }
    const goBack = () => {
        if (step > 0) {
            setStep((s) => Math.max(0, s - 1))
        }
    }

    // 2026-05-05 — Click navigation por mitades de pantalla. El click
    // (o tap) en la mitad DERECHA avanza al siguiente paso (igual que
    // el comportamiento original); el click en la mitad IZQUIERDA
    // retrocede al paso anterior. Permite a Zak revisar el rito sin
    // tener que esperar el auto-advance otra vuelta. Aplicable a
    // mobile y desktop.
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isFinalStep) return
        const w = window.innerWidth
        if (e.clientX < w / 2) {
            goBack()
        } else {
            advance()
        }
    }

    // Auto-advance entre pasos cada STEP_AUTO_MS. Solo activo durante
    // intro y mientras NO estemos en el paso final (el botón ENCARNAR
    // queda como acción explícita del Tripulante para marcar la
    // intención de descender).
    useEffect(() => {
        if (!isVisible) return
        if (isFinalStep) return
        const t = window.setTimeout(advance, STEP_AUTO_MS)
        return () => window.clearTimeout(t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, isVisible, isFinalStep])

    if (!isVisible) return null

    return (
        <div
            className="fixed inset-0 z-30"
            style={{
                userSelect: "none",
                WebkitUserSelect: "none",
                // El wrapper captura toques en cualquier parte para
                // avanzar — excepto cuando ya estamos en el botón final
                // (ahí solo el botón es interactivo).
                pointerEvents: isFinalStep ? "none" : "auto",
                cursor: isFinalStep ? "default" : "pointer",
            }}
            onClick={handleClick}
        >
            <style>{`
                @keyframes voidIntroFadeIn {
                    0%   { opacity: 0; transform: translateY(6px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
                @keyframes voidIntroButtonIn {
                    0%   { opacity: 0; transform: translateY(14px) scale(0.96); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}</style>

            {/* Título — anclado al borde superior, ancla visual de la
                sección. Visible toda la intro. */}
            <div
                className="absolute left-0 right-0 flex flex-col items-center px-6 md:px-10 text-center"
                style={{
                    top: "calc(env(safe-area-inset-top, 0px) + 6vh)",
                }}
            >
                <p
                    style={{
                        opacity: 0.85,
                        color: "rgba(180,220,255,0.7)",
                        textShadow: "0 0 18px rgba(0,194,255,0.3)",
                        animation: `voidIntroFadeIn ${FADE_MS}ms cubic-bezier(0.2, 0.7, 0.2, 1) both`,
                    }}
                    className="text-xs md:text-sm tracking-[0.32em]"
                >
                    ESTADO CERO · ANTES DE LA FORMA
                </p>
            </div>

            {/* Párrafo actual — flota entre el título y el orbe, en la
                franja media-superior (~22vh). Se vuelve a montar con
                la key del step para disparar el fade-in en cada salto. */}
            {currentParagraph && (
                <div
                    className="absolute left-0 right-0 flex flex-col items-center px-6 md:px-10 text-center"
                    style={{
                        top: "calc(env(safe-area-inset-top, 0px) + 22vh)",
                    }}
                >
                    <p
                        key={`p-${step}`}
                        style={{
                            color: "#E0F2FF",
                            textShadow: "0 0 18px rgba(0,194,255,0.3)",
                            animation: `voidIntroFadeIn ${FADE_MS}ms cubic-bezier(0.2, 0.7, 0.2, 1) both`,
                            maxWidth: 640,
                        }}
                        className="text-base md:text-xl font-light leading-relaxed"
                    >
                        {currentParagraph}
                    </p>
                </div>
            )}

            {/* Hint sutil de "tocar para continuar" mientras no es el
                paso final. Aparece centrado abajo. */}
            {!isFinalStep && (
                <div
                    className="absolute left-0 right-0 flex justify-center"
                    style={{
                        bottom:
                            "calc(env(safe-area-inset-bottom, 0px) + 6vh)",
                    }}
                >
                    <span
                        key={`hint-${step}`}
                        style={{
                            color: "rgba(180,220,255,0.55)",
                            letterSpacing: "0.28em",
                            fontSize: 11,
                            textShadow: "0 0 10px rgba(0,194,255,0.2)",
                            animation: `voidIntroFadeIn ${FADE_MS}ms cubic-bezier(0.2, 0.7, 0.2, 1) 200ms both`,
                        }}
                        className="uppercase"
                    >
                        {step + 1} / {totalParagraphSteps + 1}
                    </span>
                </div>
            )}

            {/* Botón ENCARNAR — solo en el paso final. */}
            {isFinalStep && (
                <div
                    className="absolute left-0 right-0 flex justify-center px-6"
                    style={{
                        bottom:
                            "calc(env(safe-area-inset-bottom, 0px) + 8vh)",
                        pointerEvents: "auto",
                    }}
                >
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation()
                            advanceVoidStage()
                        }}
                        style={{
                            ...buttonStyle,
                            animation: `voidIntroButtonIn 700ms cubic-bezier(0.2, 0.7, 0.2, 1) both`,
                        }}
                        aria-label="Avanzar al selector de firma"
                    >
                        ENCARNAR
                    </button>
                </div>
            )}
        </div>
    )
}

const buttonStyle: React.CSSProperties = {
    pointerEvents: "auto",
    padding: "12px 32px",
    borderRadius: 999,
    border: "1px solid rgba(0,194,255,0.55)",
    background:
        "linear-gradient(90deg, rgba(0,194,255,0.92), rgba(0,229,255,0.7))",
    color: "#04101A",
    fontFamily: "'Inter', system-ui, sans-serif",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "0.24em",
    boxShadow:
        "0 0 32px rgba(0,194,255,0.55), 0 6px 20px rgba(0,0,0,0.5)",
    cursor: "pointer",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    transition: "transform 220ms ease, box-shadow 220ms ease",
}
